/**
 * Die Host-Links — anlegen, ansehen, widerrufen.
 *
 * **Sie sind der Weg zu einer Partie.** Ein Host-Link erlaubt, eine Partie zu erstellen, und zwar
 * wiederverwendbar: ist die Partie vorbei, legt derselbe Link die nächste an. Gleichzeitig läuft
 * über jeden Link höchstens **eine** Partie — sonst könnte ein einzelner Token beliebig viele
 * Lobbys erzeugen, was ab V2 unmittelbar in LLM-Kosten umschlägt (`CLAUDE.md`,
 * Berechtigungsmodell).
 *
 * **Der Klartext erscheint genau einmal.** Gespeichert ist serverseitig nur sein Hash; ein
 * verlorener Link wird ersetzt, nicht wiederhergestellt. Deshalb zeigt diese Komponente ihn sofort
 * und fertig zusammengesetzt an — und legt ihn **nirgends** ab. Ihn „zur Bequemlichkeit" im
 * Browser zu halten wäre eine zweite Kopie eines Geheimnisses, das genau einmal existieren soll.
 *
 * **Der Token steht im Fragment, nicht im Pfad** (`ARCHITECTURE.md` **E26**): ein Pfad landet im
 * Zugriffslog von nginx und im `Referer`, ein Fragment schickt der Browser nie an den Server.
 * Damit bleibt der Link teilbar, ohne eine Spur zu hinterlassen.
 *
 * **Widerrufen ist nicht löschen.** Die Zeile bleibt stehen, damit nachvollziehbar bleibt, über
 * welchen Link eine Partie entstanden ist — ein widerrufener Link wird als solcher gezeigt und
 * bietet keine Schaltfläche mehr an.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { HostTokenCreatedDto, HostTokenDto } from '@flaghex/contracts';

import { useRequest } from '../api/context.js';
import { createHostToken, hostTokens, revokeHostToken } from '../api/endpoints.js';
import type { ApiFailure } from '../api/transport.js';

export interface HostTokenListProps {
  readonly adminKey: string;
  readonly onUnauthenticated: () => void;
}

/** Was die Angaben eines Eintrags trennt. Ohne Trennung laufen sie im Browser ineinander. */
const SEPARATOR = ' \u00b7 ';

/** Ein Zeitpunkt für Menschen. ISO ist der Vertrag, dies die Anzeige. */
function when(iso: string): string {
  return new Date(iso).toLocaleString('de-DE');
}

/** Der fertige Link, wie er weitergegeben wird — der Token im Fragment. */
export function hostLinkFor(token: string, origin: string): string {
  return `${origin}/#/host/${token}`;
}

export function HostTokenList({ adminKey, onUnauthenticated }: HostTokenListProps) {
  const { t } = useTranslation(['admin', 'errors', 'common']);
  const request = useRequest();

  const [links, setLinks] = useState<readonly HostTokenDto[] | undefined>(undefined);
  const [label, setLabel] = useState('');
  const [created, setCreated] = useState<HostTokenCreatedDto | undefined>(undefined);
  const [failure, setFailure] = useState<ApiFailure | undefined>(undefined);

  const load = useCallback(async (): Promise<void> => {
    const result = await hostTokens(request, adminKey);
    if (result.kind === 'ok') {
      setLinks(result.value);
      return;
    }
    if (result.code === 'UNAUTHENTICATED') {
      onUnauthenticated();
      return;
    }
    setFailure(result);
  }, [request, adminKey, onUnauthenticated]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = useCallback((): void => {
    const trimmed = label.trim();
    if (trimmed === '') return;
    setFailure(undefined);
    void createHostToken(request, adminKey, trimmed).then(async (result) => {
      if (result.kind === 'ok') {
        setCreated(result.value);
        setLabel('');
        await load();
        return;
      }
      if (result.code === 'UNAUTHENTICATED') {
        onUnauthenticated();
        return;
      }
      setFailure(result);
    });
  }, [label, request, adminKey, load, onUnauthenticated]);

  const revoke = useCallback(
    (id: string): void => {
      setFailure(undefined);
      void revokeHostToken(request, adminKey, id).then(async (result) => {
        if (result.kind === 'ok') {
          await load();
          return;
        }
        if (result.code === 'UNAUTHENTICATED') {
          onUnauthenticated();
          return;
        }
        setFailure(result);
      });
    },
    [request, adminKey, load, onUnauthenticated],
  );

  return (
    <section>
      <h2>{t('admin:hostLinks.heading')}</h2>
      <p>{t('admin:hostLinks.intro')}</p>

      <p>
        <label>
          {t('admin:hostLinks.label')}{' '}
          <input
            type="text"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
        </label>{' '}
        <button type="button" onClick={create} disabled={label.trim() === ''}>
          {t('admin:hostLinks.create')}
        </button>
      </p>
      <p>{t('admin:hostLinks.labelHint')}</p>

      {created === undefined ? null : (
        <div role="alert">
          <h3>{t('admin:hostLinks.createdHeading')}</h3>
          <p>
            <code>{hostLinkFor(created.token, window.location.origin)}</code>
          </p>
          <p>{t('admin:hostLinks.createdHint')}</p>
        </div>
      )}

      {failure === undefined ? null : <p role="alert">{t(`errors:${failure.code}`)}</p>}

      {links === undefined ? (
        <p role="status">{t('common:loading')}</p>
      ) : links.length === 0 ? (
        <p>{t('admin:hostLinks.none')}</p>
      ) : (
        <ul>
          {links.map((link) => (
            // Die Trennzeichen sind keine Kosmetik: ohne sie stand im Browser
            // „Rezept-Probeangelegt am 4.10.2026" — und kein Test war darüber rot.
            <li key={link.id}>
              <span>{link.label}</span>
              {SEPARATOR}
              <span>{t('admin:hostLinks.createdAt', { when: when(link.createdAt) })}</span>
              {SEPARATOR}
              <span>
                {link.lastUsedAt === undefined
                  ? t('admin:hostLinks.neverUsed')
                  : t('admin:hostLinks.lastUsedAt', { when: when(link.lastUsedAt) })}
              </span>
              {SEPARATOR}
              {link.revokedAt === undefined ? (
                <button type="button" onClick={() => revoke(link.id)}>
                  {t('admin:hostLinks.revoke')}
                </button>
              ) : (
                <span>{t('admin:hostLinks.revoked', { when: when(link.revokedAt) })}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
